"use client";

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState, useRef } from "react";
import { useApi } from "@/hooks/useApi"; // Adjust path as needed
import { useWorkspace } from "@/providers/WorkspaceContext";

export type ThemeMode = "light" | "dark" | "system";
export interface ColorTheme {
  id: string;
  name: string;
  gradient: string;
  value: string;
}

// Default values
const DEFAULTS = {
  appearance: {
    themeMode: "light"
  },
  messages: {
    userTheme: {
      background: "bg-linear-to-r from-[#FFDDF9] to-[#FFF0FC]",
      text: "text-gray-900"
    },
    coachTheme: {
      background: "bg-white",
      text: "text-gray-900"
    }
  },
  preferences: {
    language: "en",
    dateFormat: "MM/DD/YYYY",
    timeFormat: "12h",
    notifications: {
      email: true,
      push: true,
      messages: true
    },
    timezone: {
      value: "Europe/London",
      label: "Europe/London (GMT+0:00)"
    }
  }
};

// Supported languages
export type Language = "en" | "es" | "fr" | "de" | "zh" | "ja";
export const languages = [
  { id: "en", name: "English" },
  { id: "es", name: "Español" },
  { id: "fr", name: "Français" },
  { id: "de", name: "Deutsch" },
  { id: "zh", name: "中文" },
  { id: "ja", name: "日本語" }
];

// Available color themes
export const colorThemes = [
  {
    id: "green",
    name: "Fresh Green",
    gradient: "bg-linear-to-r from-[#A3EBAA] to-[#C7F4C2]",
    value: "bg-linear-to-r from-[#A3EBAA] to-[#C7F4C2] hover:shadow-lg"
  },
  {
    id: "purple",
    name: "Soft Purple",
    gradient: "bg-linear-to-r from-[#AF7FCB] to-[#DDA1FF]",
    value: "bg-linear-to-r from-[#AF7FCB] to-[#DDA1FF] hover:shadow-lg"
  },
  {
    id: "yellow",
    name: "Soft Yellow",
    gradient: "bg-linear-to-r from-[#FFE381] to-[#FFE9AA]",
    value: "bg-linear-to-r from-[#FFE381] to-[#FFE9AA] hover:shadow-lg"
  },
  {
    id: "blue",
    name: "Blue Sky",
    gradient: "bg-linear-to-r from-[#B9F2FF] to-[#65DCF8]",
    value: "bg-linear-to-r from-[#B9F2FF] to-[#65DCF8] hover:shadow-lg"
  },
  {
    id: "pink",
    name: "Soft Pink",
    gradient: "bg-linear-to-r from-[#FFDDF9] to-[#FFF0FC]",
    value: "bg-linear-to-r from-[#FFDDF9] to-[#FFF0FC] hover:shadow-lg"
  },
  {
    id: "white",
    name: "White",
    gradient: "bg-white shadow-lg border-1",
    value: "bg-white hover:shadow-lg"
  },
];

const THEME_STORAGE_KEY = "theme-preferences";
const SYNC_TIMESTAMP_KEY = "theme-sync-timestamp";
// Cache duration in milliseconds - 1 hour
const CACHE_DURATION = 60 * 60 * 1000;

interface ThemeContextType {
  // Message themes
  userMessageTheme: string;
  coachMessageTheme: string;
  userTextColor: string;
  coachTextColor: string;
  setUserMessageTheme: (themeId: string) => Promise<void>;
  setCoachMessageTheme: (themeId: string) => Promise<void>;
  setUserTextColor: (color: string) => Promise<void>;
  setCoachTextColor: (color: string) => Promise<void>;

  // Global theme mode
  themeMode: ThemeMode;
  setThemeMode: (mode: ThemeMode) => Promise<void>;
  isDarkMode: boolean;

  // Language
  language: Language;
  setLanguage: (lang: Language) => Promise<void>;

  // Helpers
  getThemeValue: (themeId: string) => string;
  isLoadingTheme: boolean;
  resetToDefaults: () => Promise<void>;

  // All settings
  settings: {
    appearance: {
      themeMode: ThemeMode;
    };
    messages: {
      userTheme: {
        background: string;
        text: string;
      };
      coachTheme: {
        background: string;
        text: string;
      };
    };
    preferences: {
      language: Language;
      dateFormat: string;
      timeFormat: string;
      notifications: {
        email: boolean;
        push: boolean;
        messages: boolean;
      };
      timezone: {
        value: string;
        label: string;
      };
    };
  };

  // Setting updater
  updateSetting: (path: string[], value: any) => Promise<void>;
}

// Create the context
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Helper to deeply set a value in a nested object
const setNestedValue = (obj: any, path: string[], value: any): any => {
  if (path.length === 0) return value;

  const [head, ...tail] = path;
  const result = { ...obj };

  if (tail.length === 0) {
    result[head] = value;
  } else {
    result[head] = setNestedValue(obj[head] || {}, tail, value);
  }

  return result;
};

// Helper to get a value from a nested object
const getNestedValue = (obj: any, path: string[]): any => {
  if (path.length === 0 || !obj) return obj;

  const [head, ...tail] = path;
  return getNestedValue(obj[head], tail);
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { request, isLoading: apiLoading } = useApi();
  const { currentWorkspace } = useWorkspace();
  const lastSyncRef = useRef<number | null>(null);
  const initialLoadDoneRef = useRef(false);
  const syncInProgressRef = useRef(false);

  // State for nested settings structure
  const [settings, setSettings] = useState({
    appearance: { ...DEFAULTS.appearance },
    messages: { ...DEFAULTS.messages },
    preferences: { ...DEFAULTS.preferences }
  });

  const [isLoadingTheme, setIsLoadingTheme] = useState(true);
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Extract values for backward compatibility
  const userMessageTheme = settings.messages.userTheme.background;
  const coachMessageTheme = settings.messages.coachTheme.background;
  const userTextColor = settings.messages.userTheme.text;
  const coachTextColor = settings.messages.coachTheme.text;
  const themeMode = settings.appearance.themeMode;
  const language = settings.preferences.language;

  // Helper to get theme value from ID
  const getThemeValue = useCallback((themeId: string): string => {
    // Check if it's a direct tailwind class
    if (themeId.startsWith("bg-")) {
      return themeId;
    }

    // Check if it's a theme ID
    const theme = colorThemes.find(t => t.id === themeId);
    if (theme) {
      return theme.gradient;
    }

    // Fallback
    return themeId === "user"
      ? DEFAULTS.messages.userTheme.background
      : DEFAULTS.messages.coachTheme.background;
  }, []);

  // Helper to save settings to localStorage
  const saveToLocalStorage = useCallback((data: any) => {
    if (typeof window === "undefined") return;

    try {
      localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(data));
      // Update sync timestamp
      localStorage.setItem(SYNC_TIMESTAMP_KEY, Date.now().toString());
      lastSyncRef.current = Date.now();
    } catch (error) {
      console.error("Error saving settings to localStorage:", error);
    }
  }, []);

  // Apply dark mode changes when theme mode changes
  useEffect(() => {
    if (typeof window === "undefined") return;

    // For system preference, check media query
    if (themeMode === "system") {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      const handleChange = (e: MediaQueryListEvent) => {
        setIsDarkMode(e.matches);
        document.documentElement.classList.toggle("dark", e.matches);
      };

      // Initial check
      setIsDarkMode(mediaQuery.matches);
      document.documentElement.classList.toggle("dark", mediaQuery.matches);

      // Set up listener for changes
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    } else {
      // Direct setting
      const isDark = themeMode === "dark";
      setIsDarkMode(isDark);
      document.documentElement.classList.toggle("dark", isDark);
    }
  }, [themeMode]);

  // Sync settings with backend
  const syncWithBackend = useCallback(async (force = false) => {
    if (typeof window === "undefined" || !currentWorkspace?.id) return;
    if (syncInProgressRef.current) return; // Prevent concurrent syncs

    // Check cache timestamp
    if (!force) {
      const lastSyncTimestamp = lastSyncRef.current || Number(localStorage.getItem(SYNC_TIMESTAMP_KEY) || 0);
      const now = Date.now();

      // If cache is still valid, skip the sync
      if (now - lastSyncTimestamp < CACHE_DURATION) {
        return;
      }
    }

    try {
      syncInProgressRef.current = true;
      setIsLoadingTheme(true);
      console.log("Fetching settings from backend...");

      const response = await request(`/api/workspaces/${currentWorkspace.id}/profiles/me/`, {
        method: "GET"
      });

      if (!response.error && response.data) {
        // Extract the data from your specific API response structure
        const profileData = response.data.data || response.data;
        console.log("Received data from backend:", profileData);

        // Start with default settings
        let newSettings = {
          appearance: { ...DEFAULTS.appearance },
          messages: { ...DEFAULTS.messages },
          preferences: { ...DEFAULTS.preferences }
        };

        // Handle top-level theme mode
        if (profileData.theme_preference) {
          newSettings.appearance.themeMode = profileData.theme_preference;
          console.log("Set theme mode from top level:", profileData.theme_preference);
        }

        // Handle top-level language
        if (profileData.language_preference) {
          newSettings.preferences.language = profileData.language_preference;
          console.log("Set language from top level:", profileData.language_preference);
        }

        // Process the preferences object if it exists
        if (profileData.preferences) {
          console.log("Processing preferences:", profileData.preferences);

          // Process messages section
          if (profileData.preferences.messages) {
            const messages = profileData.preferences.messages;

            // Process user theme
            if (messages.userTheme) {
              if (messages.userTheme.background) {
                newSettings.messages.userTheme.background = messages.userTheme.background;
                console.log("Set user background to:", messages.userTheme.background);
              }
              if (messages.userTheme.text) {
                newSettings.messages.userTheme.text = messages.userTheme.text;
                console.log("Set user text to:", messages.userTheme.text);
              }
            }

            // Process coach theme
            if (messages.coachTheme) {
              if (messages.coachTheme.background) {
                newSettings.messages.coachTheme.background = messages.coachTheme.background;
                console.log("Set coach background to:", messages.coachTheme.background);
              }
              if (messages.coachTheme.text) {
                newSettings.messages.coachTheme.text = messages.coachTheme.text;
                console.log("Set coach text to:", messages.coachTheme.text);
              }
            }
          }

          // Process appearance section
          if (profileData.preferences.appearance) {
            // Merge appearance settings except themeMode which comes from top-level
            const appearance = profileData.preferences.appearance;

            // Merge any other appearance settings that might exist
            Object.keys(appearance).forEach(key => {
              if (key !== 'themeMode') { // Skip themeMode as it's handled separately
                newSettings.appearance[key] = appearance[key];
              }
            });
          }

          // Process preferences section
          if (profileData.preferences.preferences) {
            const preferences = profileData.preferences.preferences;

            // Process dateFormat
            if (preferences.dateFormat) {
              newSettings.preferences.dateFormat = preferences.dateFormat;
            }

            // Process timeFormat
            if (preferences.timeFormat) {
              newSettings.preferences.timeFormat = preferences.timeFormat;
            }

            // Process timezone
            if (preferences.timezone) {
              newSettings.preferences.timezone = preferences.timezone;
            }

            // Process notifications
            if (preferences.notifications) {
              newSettings.preferences.notifications = preferences.notifications;
            }
          }
        }

        console.log("Final processed settings:", newSettings);

        // Update state with new settings
        setSettings(newSettings);

        // Also update localStorage
        saveToLocalStorage(newSettings);

        // Update sync timestamp
        localStorage.setItem(SYNC_TIMESTAMP_KEY, Date.now().toString());
        lastSyncRef.current = Date.now();
      }
    } catch (error) {
      console.error("Error syncing preferences with backend:", error);
    } finally {
      setIsLoadingTheme(false);
      syncInProgressRef.current = false;
    }
  }, [currentWorkspace?.id, request, saveToLocalStorage]);

  // Load from localStorage on client-side - only once on mount
  useEffect(() => {
    if (typeof window === "undefined" || initialLoadDoneRef.current) return;

    const loadInitialPreferences = async () => {
      setIsLoadingTheme(true);
      try {
        let shouldFetchFromBackend = true;

        // Load theme preferences from localStorage
        const storedPrefs = localStorage.getItem(THEME_STORAGE_KEY);
        if (storedPrefs) {
          try {
            const prefs = JSON.parse(storedPrefs);
            if (Object.keys(prefs).length > 0) {
              setSettings(prevSettings => {
                const mergedSettings = {
                  appearance: {
                    ...prevSettings.appearance,
                    ...(prefs.appearance || {})
                  },
                  messages: {
                    ...prevSettings.messages,
                    ...(prefs.messages || {})
                  },
                  preferences: {
                    ...prevSettings.preferences,
                    ...(prefs.preferences || {})
                  }
                };
                console.log("Loaded settings from localStorage:", mergedSettings);
                return mergedSettings;
              });

              // Check if we still need to sync based on timestamp
              const lastSync = Number(localStorage.getItem(SYNC_TIMESTAMP_KEY) || 0);
              const now = Date.now();
              shouldFetchFromBackend = !lastSync || (now - lastSync > CACHE_DURATION);
            }
          } catch (parseError) {
            console.error("Error parsing stored preferences:", parseError);
            // On parse error, we should fetch from backend
            shouldFetchFromBackend = true;
          }
        } else {
          // No preferences in localStorage, definitely fetch from backend
          shouldFetchFromBackend = true;
        }

        // Sync with backend if needed and workspace is available
        if (shouldFetchFromBackend && currentWorkspace?.id) {
          console.log("Fetching settings from backend - localStorage empty or expired");
          await syncWithBackend(true); // Force sync
        }
      } catch (error) {
        console.error("Error loading theme preferences:", error);
      } finally {
        setIsLoadingTheme(false);
        initialLoadDoneRef.current = true;
      }
    };

    loadInitialPreferences();
  }, [currentWorkspace?.id, syncWithBackend]);

  // Generic setting updater that works with nested paths
  const updateSetting = useCallback(async (path: string[], value: any): Promise<void> => {
    console.log(`Updating setting: ${path.join('.')} = `, value);

    // Create a copy of current settings to make updates
    const updatedSettings = setNestedValue({...settings}, path, value);

    // Update state with new value
    setSettings(prevSettings => {
      console.log("Updating settings from:", prevSettings);
      console.log("To:", updatedSettings);
      return {...updatedSettings};
    });

    // Save to localStorage
    saveToLocalStorage(updatedSettings);

    // Prepare backend data
    if (currentWorkspace?.id) {
      try {
        // Determine if this is a top-level setting or preferences
        let updateData: any = {};

        // Special cases for top-level fields
        if (path[0] === 'appearance' && path[1] === 'themeMode') {
          updateData.theme_preference = value;
        } else if (path[0] === 'preferences' && path[1] === 'language') {
          updateData.language_preference = value;
        } else {
          // Structure the preferences object in the format expected by your backend
          let preferencesData: any = {
            messages: {
              userTheme: { ...settings.messages.userTheme },
              coachTheme: { ...settings.messages.coachTheme }
            },
            appearance: { ...settings.appearance },
            preferences: { ...settings.preferences }
          };

          // Remove themeMode from appearance as it's a top-level field
          delete preferencesData.appearance.themeMode;

          // Remove language from preferences as it's a top-level field
          delete preferencesData.preferences.language;

          // Update the specific path in the preferences data
          if (path[0] === 'messages') {
            if (path[1] === 'userTheme') {
              if (path[2] === 'background') {
                preferencesData.messages.userTheme.background = value;
              } else if (path[2] === 'text') {
                preferencesData.messages.userTheme.text = value;
              }
            } else if (path[1] === 'coachTheme') {
              if (path[2] === 'background') {
                preferencesData.messages.coachTheme.background = value;
              } else if (path[2] === 'text') {
                preferencesData.messages.coachTheme.text = value;
              }
            }
          } else if (path[0] === 'preferences') {
            if (path[1] === 'notifications') {
              if (path.length > 2) {
                // Update a specific notification setting
                preferencesData.preferences.notifications[path[2]] = value;
              } else {
                // Update all notification settings
                preferencesData.preferences.notifications = value;
              }
            } else if (['dateFormat', 'timeFormat', 'timezone'].includes(path[1])) {
              preferencesData.preferences[path[1]] = value;
            }
          }

          updateData.preferences = preferencesData;
        }

        console.log("Sending to backend:", updateData);

        // Make API request with the request utility
        await request(`/api/workspaces/${currentWorkspace.id}/profiles/me/`, {
          method: "PATCH",
          body: JSON.stringify(updateData),
        });

        // Update sync timestamp
        localStorage.setItem(SYNC_TIMESTAMP_KEY, Date.now().toString());
        lastSyncRef.current = Date.now();
      } catch (error) {
        console.error(`Error syncing setting with backend:`, error);
        // Continue despite error - we've already updated local state
      }
    }

    return Promise.resolve();
  }, [currentWorkspace?.id, request, saveToLocalStorage, settings]);

  // Backward compatibility setters
  const setUserMessageTheme = useCallback(async (themeId: string): Promise<void> => {
    return updateSetting(['messages', 'userTheme', 'background'], themeId);
  }, [updateSetting]);

  const setCoachMessageTheme = useCallback(async (themeId: string): Promise<void> => {
    return updateSetting(['messages', 'coachTheme', 'background'], themeId);
  }, [updateSetting]);

  const setUserTextColor = useCallback(async (color: string): Promise<void> => {
    return updateSetting(['messages', 'userTheme', 'text'], color);
  }, [updateSetting]);

  const setCoachTextColor = useCallback(async (color: string): Promise<void> => {
    return updateSetting(['messages', 'coachTheme', 'text'], color);
  }, [updateSetting]);

  const setThemeMode = useCallback(async (mode: ThemeMode): Promise<void> => {
    return updateSetting(['appearance', 'themeMode'], mode);
  }, [updateSetting]);

  const setLanguage = useCallback(async (lang: Language): Promise<void> => {
    return updateSetting(['preferences', 'language'], lang);
  }, [updateSetting]);

  // Reset to defaults
  const resetToDefaults = useCallback(async (): Promise<void> => {
    const defaultSettings = {
      appearance: { ...DEFAULTS.appearance },
      messages: { ...DEFAULTS.messages },
      preferences: { ...DEFAULTS.preferences }
    };

    // Update state
    setSettings(defaultSettings);

    // Reset localStorage
    saveToLocalStorage(defaultSettings);

    // Sync with backend
    if (currentWorkspace?.id) {
      try {
        // Simply update the profile with default values
        await request(`/api/workspaces/${currentWorkspace.id}/profiles/me/`, {
          method: "PATCH",
          body: JSON.stringify({
            theme_preference: DEFAULTS.appearance.themeMode,
            language_preference: DEFAULTS.preferences.language,
            preferences: defaultSettings
          }),
        });

        // Update sync timestamp
        localStorage.setItem(SYNC_TIMESTAMP_KEY, Date.now().toString());
        lastSyncRef.current = Date.now();
      } catch (error) {
        console.error("Error resetting preferences with backend:", error);
        // Continue despite error - we've already updated local state
      }
    }

    return Promise.resolve();
  }, [currentWorkspace?.id, request, saveToLocalStorage]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    // Backward compatibility
    userMessageTheme,
    coachMessageTheme,
    userTextColor,
    coachTextColor,
    setUserMessageTheme,
    setCoachMessageTheme,
    setUserTextColor,
    setCoachTextColor,
    themeMode,
    setThemeMode,
    isDarkMode,
    language,
    setLanguage,

    // Helpers
    getThemeValue,
    isLoadingTheme: isLoadingTheme || apiLoading,
    resetToDefaults,

    // New structured settings
    settings,
    updateSetting
  }), [
    userMessageTheme,
    coachMessageTheme,
    userTextColor,
    coachTextColor,
    setUserMessageTheme,
    setCoachMessageTheme,
    setUserTextColor,
    setCoachTextColor,
    themeMode,
    setThemeMode,
    isDarkMode,
    language,
    setLanguage,
    getThemeValue,
    isLoadingTheme,
    apiLoading,
    resetToDefaults,
    settings,
    updateSetting
  ]);

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

// Custom hook for easier consumption
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
